import { Canvas } from "@react-three/fiber";
import { Grid, Stats } from "@react-three/drei";
import { DoubleSide, Vector3 } from "three";
import { CameraFrustums } from "./camera-frustums";
import { MapControls } from "./controls/map-controls";
import { getDefaultCameraProps } from "./utils";
import { memo, useEffect, useMemo, useRef } from "react";
import { useViewSettings } from "@/view-settings";
import { useSelector } from "@legendapp/state/react";
import { Tracks } from "./tracks";
import { StoreModel } from "./store-model";
import { Zones } from "@/virtual-store/zones/zones";
import { Planogram } from "@/virtual-store/planogram/planogram";
import { useCalibration } from "./hooks/useCalibration";
import { Spinner, Text } from "@radix-ui/themes";
import { twMerge } from "tailwind-merge";
import { InfoItem } from "@/components/info-item";
import {
  calibrationTunnel,
  loadingProgressTunnel,
} from "@/virtual-store/state";

const SceneObjects = memo(() => {
  const node = useRef(document.createElement("div"));

  useEffect(() => {
    node.current.id = "test";
    const container = document.querySelector(
      ".virtual-store"
    ) as HTMLDivElement;

    container.appendChild(node.current);

    return () => {
      container.removeChild(node.current);
    };
  }, []);

  return (
    <>
      <Stats className="absolute" parent={node} />
      <axesHelper args={[10]} />
      <Grid
        infiniteGrid
        args={[60, 60]}
        position={[0, 0.02, 0]}
        side={DoubleSide}
      />
      <Tracks />
      <Planogram />
      <Zones />
    </>
  );
});

function VirtualStoreInner() {
  const controlsRef = useRef<{ setTarget: (vector: Vector3) => void }>();
  const { calibration, isLoading } = useCalibration();

  const camera = useMemo(() => getDefaultCameraProps(), []);

  return (
    <>
      <Canvas
        key="virtual-store"
        className="virtual-store"
        frameloop="demand"
        raycaster={{ params: { Points: { threshold: 0.025 } } }}
        gl={{ antialias: true }}
        resize={{ debounce: 0 }}
        camera={camera}
      >
        {calibration && (
          <>
            <StoreModel calibrationId={calibration.id} />
            <CameraFrustums
              cameraCalibrations={calibration.camera_calibrations}
              updateControlTarget={(vector) =>
                controlsRef.current?.setTarget(vector)
              }
            />
          </>
        )}
        <SceneObjects />
        <MapControls ref={controlsRef} />
      </Canvas>
      <div
        className={twMerge([
          "absolute top-0 left-0 w-full h-full bg-black text-gray-50 z-100 opacity-0 transition-opacity duration-500",
          isLoading ? "opacity-1" : "opacity-0 pointer-events-none",
        ])}
      >
        <div className="flex justify-center items-center h-full gap-2">
          <Spinner size="3" />
          <Text size="2">Loading, please wait...</Text>
        </div>
      </div>
      <calibrationTunnel.In>
        {calibration && (
          <>
            <Text size="1" weight="bold">
              Calibration:
            </Text>
            <InfoItem label="ID" value={calibration.id} />
            <InfoItem
              label="Number of cameras"
              value={calibration.number_of_cameras}
            />
            <InfoItem label="Description" value={calibration.description} />
            <InfoItem label="Created at" value={calibration.created_at} />
          </>
        )}
      </calibrationTunnel.In>
      <loadingProgressTunnel.Out />
    </>
  );
}

export function VirtualStore() {
  const viewSettings$ = useViewSettings();
  const isVisible = useSelector(
    () => viewSettings$.viewMode.get() === "snapshots"
  );

  if (!isVisible) {
    return null;
  }

  return <VirtualStoreInner />;
}
